/**
 * ChatAttachments — attach button, drag-and-drop, paste-an-image, and the picture/file chips (SUN-82).
 * Shared by the agent chat threads (AgentThreadsPanel) and Team channels (TeamTab).
 *
 *   useChatUploads(upload)  → pending files, upload state, and handlers for the button / drop / paste
 *   <PendingFiles/>         → the files waiting to be sent, each with a remove ×
 *   <AttachButton/>         → the 📎 button
 *   <MessageFiles/>         → files of a sent message: a thumbnail for pictures, a chip for the rest
 *
 * The server checks everything again (type by content, 15 MB, 5 per message, no programs or archives,
 * no ID papers); the checks here only give an instant answer for the simple cases.
 */
import { useCallback, useRef, useState } from 'react';
import { kiaanServerText, type ChatAttachment } from './kiaanApi';

export const MAX_FILES = 5;
export const MAX_BYTES = 15 * 1024 * 1024;
export const ACCEPT = '.png,.jpg,.jpeg,.webp,.heic,.heif,.pdf,.docx,.xlsx,.csv,.txt,.md,image/*';

const sizeText = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export interface ChatUploads {
  pending: ChatAttachment[];
  busy: boolean;
  error: string | null;
  add: (files: File[]) => Promise<void>;
  remove: (id: string) => void;
  clear: () => void;
  /** Spread on the area that accepts drops. */
  dropProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    /** "true" while a file is dragged over the area (SUN-91): kiaan.css shows "Drop to attach". */
    'data-dropping'?: 'true';
  };
  onPaste: (e: React.ClipboardEvent) => void;
}

export function useChatUploads(upload: ((files: File[]) => Promise<ChatAttachment[]>) | null): ChatUploads {
  const [pending, setPending] = useState<ChatAttachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The count must be known inside add() even when two drops arrive close together.
  const count = useRef(0);
  // dragenter/dragleave also fire for every child; the depth counter keeps the highlight steady.
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const hasFiles = (e: React.DragEvent) => !!e.dataTransfer?.types?.includes('Files');

  const add = useCallback(async (files: File[]) => {
    if (!upload || files.length === 0) return;
    if (count.current + files.length > MAX_FILES) { setError(`At most ${MAX_FILES} files in one message.`); return; }
    const big = files.find((f) => f.size > MAX_BYTES);
    if (big) { setError(`"${big.name || 'The picture'}" is bigger than 15 MB.`); return; }
    setBusy(true);
    setError(null);
    try {
      const saved = await upload(files);
      count.current += saved.length;
      setPending((p) => [...p, ...saved]);
    } catch (err) {
      setError(kiaanServerText(err));
    } finally {
      setBusy(false);
    }
  }, [upload]);

  const remove = (id: string) => setPending((p) => { const next = p.filter((a) => a.id !== id); count.current = next.length; return next; });
  const clear = () => { count.current = 0; setPending([]); setError(null); };

  return {
    pending, busy, error, add, remove, clear,
    dropProps: {
      ...(dragging && upload ? { 'data-dropping': 'true' as const } : {}),
      onDragEnter: (e) => { if (upload && hasFiles(e)) { depth.current += 1; setDragging(true); } },
      onDragLeave: (e) => {
        if (!upload || !hasFiles(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      },
      onDragOver: (e) => { if (upload && hasFiles(e)) { e.preventDefault(); setDragging(true); } },
      onDrop: (e) => {
        depth.current = 0;
        setDragging(false);
        const files = Array.from(e.dataTransfer?.files ?? []);
        if (!upload || files.length === 0) return;
        e.preventDefault();
        void add(files);
      },
    },
    onPaste: (e) => {
      if (!upload) return;
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (files.length === 0) return; // plain text paste stays normal
      e.preventDefault();
      void add(files);
    },
  };
}

export function AttachButton({ uploads, disabled, title }: { uploads: ChatUploads; disabled?: boolean; title?: string }) {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        data-testid="attach-input"
        onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ''; void uploads.add(f); }}
      />
      <button
        type="button"
        className="kw-icon-btn"
        aria-label="Attach a file or picture"
        title={title ?? 'Attach a file or picture (or drop / paste one)'}
        disabled={disabled || uploads.busy || uploads.pending.length >= MAX_FILES}
        onClick={() => input.current?.click()}
      >
        {uploads.busy ? '…' : '📎'}
      </button>
    </>
  );
}

export function PendingFiles({ uploads }: { uploads: ChatUploads }) {
  if (uploads.pending.length === 0 && !uploads.error && !uploads.busy) return null;
  return (
    <div className="kw-files-pending" data-testid="pending-files">
      {uploads.pending.map((a) => (
        <span key={a.id} className="kw-fchip" data-testid="pending-file">
          {a.kind === 'image' ? '🖼' : '📄'} {a.name} <small>{sizeText(a.size)}</small>
          <button type="button" className="kw-x" aria-label={`Remove ${a.name}`} onClick={() => uploads.remove(a.id)}>×</button>
        </span>
      ))}
      {uploads.busy && <span className="kw-small">Uploading…</span>}
      {uploads.error && <span className="kw-small" role="alert" style={{ color: 'var(--danger)' }}>{uploads.error}</span>}
    </div>
  );
}

/** Files of a sent message: pictures as thumbnails (click opens full size), other files as chips. */
export function MessageFiles({ files }: { files?: ChatAttachment[] }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="kw-files" data-testid="message-files">
      {files.map((a) =>
        a.kind === 'image' && a.url ? (
          <a key={a.id} href={a.url} target="_blank" rel="noreferrer" title={a.name} data-testid="file-thumb">
            <img src={a.url} alt={a.name} className="kw-thumb" loading="lazy" />
          </a>
        ) : (
          <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="kw-fchip" data-testid="file-chip">
            📄 {a.name} <small>{sizeText(a.size)}</small>
          </a>
        ),
      )}
    </div>
  );
}
